import * as React from 'react';

import { cx } from '../../utils/cx';

interface IChatInputProps {
  onSend: (message: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
}

/**
 * Chat input with send button. Supports Enter to send (Shift+Enter for newline).
 */
const ChatInput = ({ onSend, isDisabled, placeholder = 'Ask about your finances...' }: IChatInputProps) => {
  const [value, setValue] = React.useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea as content grows
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [value]);

  const handleSubmit = () => {
    if (!value.trim() || isDisabled) {
      return;
    }

    onSend(value.trim());
    setValue('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="bg-primary border-secondary flex items-end gap-3 rounded-2xl border p-3 shadow-sm">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isDisabled}
        rows={1}
        className={cx(
          'placeholder:text-placeholder text-primary flex-1 resize-none bg-transparent text-sm outline-none',
          'max-h-[200px] min-h-[24px]',
          isDisabled && 'cursor-not-allowed opacity-50'
        )}
      />
      <button
        onClick={handleSubmit}
        disabled={isDisabled || !value.trim()}
        className={cx(
          'flex size-8 shrink-0 items-center justify-center rounded-full transition-colors',
          value.trim() && !isDisabled
            ? 'bg-brand-solid hover:bg-brand-solid_hover cursor-pointer text-white'
            : 'bg-disabled text-disabled cursor-not-allowed'
        )}
      >
        <SendIcon className="size-4" />
      </button>
    </div>
  );
};

/**
 * Simple send arrow icon
 */
const SendIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

export default ChatInput;
